import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from '../store/hooks';
import { faqItems } from '../store/data';

function FaqAccordion({ question, answer, index, isOpen, onToggle }: {
  question: string;
  answer: string;
  index: number;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.1 + index * 0.06 }}
      className={`glass-card-light rounded-xl overflow-hidden transition-all duration-300 ${
        isOpen ? 'border-[#c8ff00]/20 bg-[#c8ff00]/[0.02]' : 'hover:border-white/10'
      }`}
    >
      <button
        onClick={onToggle}
        className="w-full text-left px-6 py-5 flex items-start gap-4 group"
        aria-expanded={isOpen}
      >
        {/* Number */}
        <span className={`flex-shrink-0 font-body text-sm font-semibold mt-0.5 transition-colors duration-300 ${
          isOpen ? 'text-[#c8ff00]' : 'text-surface-600 group-hover:text-surface-400'
        }`}>
          {String(index + 1).padStart(2, '0')}
        </span>

        {/* Question */}
        <span className={`flex-1 font-body font-semibold text-[15px] leading-relaxed transition-colors duration-300 ${
          isOpen ? 'text-white' : 'text-surface-300 group-hover:text-white'
        }`}>
          {question}
        </span>

        {/* Toggle icon */}
        <span className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition-all duration-300 mt-0.5 ${
          isOpen
            ? 'bg-[#c8ff00] rotate-45'
            : 'bg-white/[0.06] group-hover:bg-white/10'
        }`}>
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            className={`transition-colors duration-300 ${isOpen ? 'text-surface-900' : 'text-surface-400'}`}
          >
            <path d="M6 1V11M1 6H11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </span>
      </button>

      {/* Answer */}
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
          >
            <div className="px-6 pb-6 pl-[52px]">
              <p className="font-body text-sm text-surface-400 leading-relaxed">
                {answer}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default function FAQ() {
  const { ref, isInView } = useInView(0.05);
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section id="faq" ref={ref} className="py-28 sm:py-36 relative">
      <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-white/10 to-transparent" />

      <div className="max-w-4xl mx-auto px-6">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-14"
        >
          <span className="inline-block text-xs font-body font-semibold tracking-[0.25em] uppercase text-[#c8ff00] mb-4">
            Common Questions
          </span>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight">
            Frequently <span className="text-gradient italic">Asked</span>
          </h2>
          <p className="font-body text-surface-400 mt-4 text-lg max-w-xl mx-auto">
            Everything you need to know before getting started. Can't find your answer? Reach out directly.
          </p>
        </motion.div>

        {/* Accordion grid */}
        <div className="space-y-3">
          {faqItems.map((item, i) => (
            <FaqAccordion
              key={i}
              index={i}
              question={item.question}
              answer={item.answer}
              isOpen={openIndex === i}
              onToggle={() => setOpenIndex(openIndex === i ? null : i)}
            />
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="text-center mt-12"
        >
          <p className="font-body text-surface-500 text-sm mb-4">
            Still have questions?
          </p>
          <a
            href="#contact"
            className="inline-flex items-center gap-2 px-6 py-3 border border-white/15 text-white font-body text-sm rounded-full hover:bg-white/5 transition-all duration-300 hover:-translate-y-0.5"
          >
            Let's Talk
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
              <path d="M4 12L12 4M12 4H5M12 4V11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </a>
        </motion.div>
      </div>
    </section>
  );
}
